(ns ui.components.radix-ui.dropdown-menu.view
  (:require
   ["@radix-ui/react-dropdown-menu" :as DropdownMenu]
   [reagent.core :as r]
   [ui.components.radix-ui.tooltip.view :as tooltip]
   [ui.util :as uu]))

(defn adapt
  ([cls-name]
   (r/adapt-react-class (aget DropdownMenu cls-name)))
  ([parent-cls cls-name]
   (r/adapt-react-class (aget (aget DropdownMenu parent-cls) cls-name))))

(def dropdown-menu-root (adapt "Root"))
(def dropdown-menu-trigger (adapt "Trigger"))
(def dropdown-menu-portal (adapt "Portal"))
(def dropdown-menu-content (adapt "Content"))
(def dropdown-menu-item (adapt "Item"))
#_(def dropdown-menu-item-indicator (adapt "ItemIndicator"))
(def dropdown-menu-sub (adapt "Sub"))
(def dropdown-menu-sub-trigger (adapt "SubTrigger"))
(def dropdown-menu-sub-content (adapt "SubContent"))
(def dropdown-menu-separator (adapt "Separator"))
(def dropdown-menu-label (adapt "Label"))
(def dropdown-menu-arrow (adapt "Arrow"))

(defn dropdown-menu-item-with-tooltip
  [{:keys [tooltip custom-item? on-click disabled? value icon-class label]}]
  [tooltip/tooltip-provider
   [tooltip/tooltip-root
    {:delayDuration 300}
    [tooltip/tooltip-trigger
     {:asChild true}
     [:span
      [dropdown-menu-item
       {:class    (uu/cm "mb-0 p-2 flex items-center gap-3 text-sm !outline-0 shadow-none"
                         (when-not disabled?
                           "hover:bg-base-regular")
                         (when disabled?
                           "text-disabled-regular-text cursor-not-allowed"))
        :asChild  custom-item?
        :onSelect (fn []
                    (when (fn? on-click)
                      (on-click)))
        :disabled (boolean disabled?)}
       (if custom-item?
         value
         [:<>
          (when icon-class
            [:i {:class (uu/cm icon-class) #_"m-0 w-6 flex items-center justify-center absolute left-0 text-sm"}])
          label])]]]
    [tooltip/tooltip-content
     {:class      (uu/cm "p-3 text-xs max-w-64 bg-base-regular text-base-regular-text border border-solid border-base-regular-border-paler rounded")
      :sideOffset 5
      :side       :left
      :style      {:z-index 10000002}}
     tooltip
     [tooltip/tooltip-arrow
      {:class "fill-base-regular "}]]]])

(defn some-item-has-icon?
  [items]
  (->> items
       (filter (fn [{:keys [icon-class]}]
                 (boolean icon-class)))
       (count)
       (< 0)))

(defn button-trigger
  [{:keys [dom-id label icon]}]
  [:span
   (merge {:class "mb-0 px-3 py-2 inline-flex gap-3 items-center outline-none font-semibold leading-snug rounded underline-offset-4 whitespace-nowrap cursor-pointer disabled:cursor-not-allowed select-none
            text-sm text-primary-regular-text hover:text-primary-dark-text disabled:text-disabled-regular-text
            border border-solid border-primary-regular-border hover:border-primary-gloomy-border disabled:border-disabled-regular-border
            bg-primary-regular hover:bg-primary-gloomy disabled:bg-disabled-regular"}
          (when dom-id
            {:id dom-id}))
   label
   icon])

(defn button-trigger-hollow
  [label icon]
  [:span
   {:class "mb-0 px-3 py-2 inline-flex gap-3 items-center outline-none font-semibold leading-snug rounded underline-offset-4 whitespace-nowrap cursor-pointer disabled:cursor-not-allowed select-none
            text-sm text-base-light-text hover:text-base-light-text disabled:text-disabled-regular-text
            border border-solid border-base-light-border-paler hover:border-base-light-border focus:border-base-light-border focus-within:border-base-light-border disabled:border-disabled-regular-border"}
   label
   icon])

(defn button-trigger-open
  [label icon]
  [:span
   {:class "mb-0 inline-flex gap-3 items-center outline-none font-semibold leading-snug rounded underline-offset-4 whitespace-nowrap cursor-pointer disabled:cursor-not-allowed select-none
            text-sm text-base-light-text hover:text-base-light-text-accent disabled:text-disabled-regular-text"}
   label
   icon])

(def default-menu-item-class
  (uu/cm
   "min-w-48 mb-0 p-2 flex items-center gap-3"
   "text-sm text-base-light-text hover:bg-base-regular"
   "!outline-0 shadow-nonerounded cursor-pointer"))

(def default-content-class
  (uu/cm "relative p-3 max-w-72 bg-base-light border border-solid"
         "border-base-light-border-paler rounded-md z-[300000]"
         ;; Radix exposes the space left to the viewport edge as this var;
         ;; without it the menu overflows vertically off the page.
         "max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto"))

(declare menu-item)

(defn- items-with-stable-keys
  "Positional fallback key so unkeyed items don't get a fresh gensym key every render, which would remount sub-menu-item and close open submenus."
  [items]
  (map-indexed (fn [idx item]
                 (cond-> item
                   (nil? (:key item))
                   (assoc :key (str "dropdown-menu-item-" idx))))
               items))

(defn sub-menu-item
  [_]
  (let [sub-open (r/atom false)]
    (fn [{:keys [disabled? label props items custom-class sub-menu-side hide-chevron?]}]
      (let [left? (= sub-menu-side :left)]
        [dropdown-menu-sub
         {:open @sub-open
          :on-open-change #(reset! sub-open %)}
         [dropdown-menu-sub-trigger
          {:class (uu/cm default-menu-item-class
                         (when disabled?
                           "text-disabled-regular-text cursor-not-allowed")
                         custom-class)}
          (when (and left? (not hide-chevron?))
            [:i.fal.fa-angle-left])
          label
          (when (and (not left?) (not hide-chevron?))
            [:i.fal.fa-angle-right])]
         [dropdown-menu-portal
          [dropdown-menu-sub-content
           (cond-> {:class (uu/cm default-content-class
                                  (:content-class props))
                    :sideOffset 12
                    :alignOffset -5}
             left? (assoc :side "left"))
           (doall (for [item (items-with-stable-keys items)]
                    (menu-item item)))]]]))))

(defn menu-item
  [{:keys [label on-click item-class icon-class value items key separator? custom-item?
           disabled? selected? section-label? tooltip] :as props}]
  (cond
    separator?
    ^{:key (if key
             key
             (gensym "dropdown-key-"))}
    [dropdown-menu-separator
     {:class "h-px m-1 bg-base-regular"}]
    section-label?
    ^{:key (if key
             key
             (gensym "dropdown-key-"))}
    [dropdown-menu-label
     ;; Zapflow green via the design token rather than a hex, so it follows the
     ;; active theme - `--primary-regular-text-accent` is the palette's
     ;; green-on-light text value, not the button fill (`--primary-regular`),
     ;; which is too pale to read as text.
     {:class "py-2 uppercase text-xs font-semibold text-primary-regular-text-accent"} label]
    (seq items)
    ^{:key (if key
             key
             (gensym "dropdown-key-"))}
    [sub-menu-item
     props]

    :else
    (if tooltip
      ^{:key (if key
               key
               (gensym "dropdown-key-"))}
      [dropdown-menu-item-with-tooltip
       props
       #_(assoc props :has-icon? item-has-icon?)]
      ^{:key (if key
               key
               (gensym "dropdown-key-"))}
      [dropdown-menu-item
       {:class    (uu/cm default-menu-item-class
                         item-class
                         (when disabled?
                           "text-disabled-regular-text cursor-not-allowed"))
        :asChild  custom-item?
        :onSelect (fn [e]
                    (when (fn? on-click)
                      (on-click e)))
        :disabled (boolean disabled?)}
       (if custom-item?
         value
         [:div
          {:class (uu/cm "flex flex-row items-center gap-2"
                         (when selected?
                           "text-primary-regular-text-accent"))}
          (when icon-class
            [:i {:class (uu/cm icon-class)}])
          label])])))

(defn view
  [{:keys [on-open-change]}]
  (fn [{:keys [trigger trigger-as-child items props]}]
    ;; not sure about icon logic - leaving here for reference:
    (let [_item-has-icon? (some-item-has-icon? items)]
      [dropdown-menu-root
       {:onOpenChange (fn [open?]
                        (when (fn? on-open-change)
                          (on-open-change open?)))}
       (if trigger-as-child
         [dropdown-menu-trigger
          {:asChild true}
          trigger-as-child]
         [dropdown-menu-trigger
          {:asChild false
           :class   (uu/cm "m-0 p-0 min-w-4 text-center text-base-light-text"
                           (:trigger-class props))}
          trigger])

       [dropdown-menu-portal
        [dropdown-menu-content
         {:class      (uu/cm default-content-class
                             (:content-class props))
          :align      (or (:align props) "start")
          :sideOffset (or (:side-offset props) 5)
          :side       (or (:side props) "bottom")}
         (->> items
              items-with-stable-keys
              (map
               menu-item)
              (doall))

         (when-not (:hide-arrow? props)
           [dropdown-menu-arrow
            {:class (uu/cm "fill-base-regular")}])]]])))
